import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UXP_PI_ID,
  EXPLORER_VIEWS,
  PRODUCT_DNA_DIMENSIONS,
  KNOWLEDGE_GRAPH_NODES,
  HEALTH_DIMENSIONS,
  ADVISOR_CAPABILITIES,
  PERFORMANCE_METRICS,
  AI_RECOMMENDATION_TYPES,
  WORKSPACE_REGIONS,
  BULK_OPERATIONS,
  PUBLISHING_STAGES,
  COLLABORATION_FEATURES,
  SEARCH_CAPABILITIES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  ANALYTICS_METRICS,
  LIVE_ROUTES,
  DEMO_PRODUCTS,
  DEMO_HEALTH_SCORES,
  DEMO_KG_EDGES,
  DEMO_ADVISOR_INSIGHTS,
  DEMO_RECOMMENDATIONS,
  DEMO_PERFORMANCE,
  buildProductIntelDashboard,
} from '../../productintel/framework';
import {
  ProductExplorerPanel,
  ProductDNACard,
  ProductHealthGrid,
  KnowledgeGraphPreview,
  ProductAdvisorCard,
  ProductPerformanceStrip,
  AIRecommendationsPanel,
  ProductWorkspaceNav,
} from '../../components/productintel';
import { Card, KpiCard } from '../../components/ui';

export function ProductIntelligencePage() {
  const dashboard = buildProductIntelDashboard();
  const [activeRegion, setActiveRegion] = useState('Overview');

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_PI_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Product Intelligence Workspace</h1>
        <p className="mt-1 text-sm text-slate-500">
          Product DNA engine, knowledge graph, health scoring, AI advisor, and optimization lifecycle — every product as a continuously learning business asset.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100 dark:border-brand-900 dark:bg-brand-950 dark:text-brand-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="DNA Dimensions" value={dashboard.product_dna_dimensions.toString()} />
        <KpiCard label="Health Dimensions" value={dashboard.health_dimensions.toString()} />
        <KpiCard label="Workspace Regions" value={dashboard.workspace_regions.toString()} />
        <KpiCard label="AI Rec Types" value={dashboard.ai_recommendation_types.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product Workspace</h2>
        <ProductWorkspaceNav
          regions={WORKSPACE_REGIONS}
          active={activeRegion}
          onChange={setActiveRegion}
        />
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product Explorer</h2>
        <p className="mt-1 text-sm text-slate-500">
          Views: {EXPLORER_VIEWS.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <ProductExplorerPanel products={DEMO_PRODUCTS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <ProductDNACard dimensions={PRODUCT_DNA_DIMENSIONS} confidence={87} />
        <div>
          <h2 className="text-lg font-semibold">Knowledge Graph</h2>
          <Card className="mt-4" padding="sm">
            <KnowledgeGraphPreview edges={DEMO_KG_EDGES} />
          </Card>
          <div className="mt-3 flex flex-wrap gap-2">
            {KNOWLEDGE_GRAPH_NODES.map((n) => (
              <span key={n} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{n}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product Health</h2>
        <p className="mt-1 text-sm text-slate-500">{HEALTH_DIMENSIONS.join(' · ')}</p>
        <div className="mt-4">
          <ProductHealthGrid scores={DEMO_HEALTH_SCORES} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product Performance</h2>
        <div className="mt-4">
          <ProductPerformanceStrip metrics={DEMO_PERFORMANCE} />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {PERFORMANCE_METRICS.map((m) => (
            <span key={m} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{m}</span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <ProductAdvisorCard insights={DEMO_ADVISOR_INSIGHTS} />
        <div>
          <h2 className="text-lg font-semibold">AI Recommendations</h2>
          <div className="mt-4">
            <AIRecommendationsPanel recommendations={DEMO_RECOMMENDATIONS} />
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Bulk Operations</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {BULK_OPERATIONS.map((op) => (
              <span key={op} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{op}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Publishing Pipeline</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {PUBLISHING_STAGES.map((s, i) => (
              <span key={s} className="flex items-center gap-1 text-xs">
                <span className="rounded-lg border border-orange-200 bg-orange-50 px-2 py-1 text-orange-700 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300">{s}</span>
                {i < PUBLISHING_STAGES.length - 1 && <span className="text-slate-400">→</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {COLLABORATION_FEATURES.map((f) => (
              <li key={f}>✓ {f}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Search</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {SEARCH_CAPABILITIES.map((s) => (
              <span key={s} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{s}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <li key={s}>✓ {s}</li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Advisor Capabilities</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ADVISOR_CAPABILITIES.map((c) => (
            <span key={c} className="rounded-lg border border-ai-200 bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:border-ai-800 dark:bg-ai-950 dark:text-ai-300">{c}</span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Recommendation Types</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {AI_RECOMMENDATION_TYPES.map((t) => (
            <span key={t} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{t}</span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Analytics Metrics</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ANALYTICS_METRICS.map((m) => (
            <span key={m} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{m}</span>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500">
        Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/uxp/UXP-00004_PRODUCT_INTELLIGENCE_WORKSPACE.md</code>
        {' · '}
        Registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/productintel/</code>
        {' · '}
        DNA Engine: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/aicatalog/productdna.go</code>
      </p>
    </div>
  );
}
